import { ImportWizardState } from '../components/types';

const STORAGE_KEY_PREFIX = 'data-import-mapping-';
const STORAGE_VERSION = '1.0';

interface StoredMappingState {
  version: string;
  studyId: number;
  wizardState: ImportWizardState;
  savedAt: string;
}

/**
 * Get localStorage key for a specific study
 */
const getStorageKey = (studyId: number): string => {
  return `${STORAGE_KEY_PREFIX}${studyId}`;
};

/**
 * Save mapping state to localStorage for a specific study
 */
export const saveMappingState = (
  studyId: number,
  wizardState: ImportWizardState
): void => {
  try {
    // Don't save File objects - they can't be serialized
    // Only save files from csv-importer (they have csvFile.id)
    const serializedState: ImportWizardState = {
      ...wizardState,
      selectedFiles: wizardState.selectedFiles.map(file => {
        // Keep only files from csv-importer (they can be restored via API)
        if (file.source === 'csv-importer' && file.csvFile) {
          return {
            ...file,
            // Remove File object if present
            file: undefined,
            // Keep csvFile but without File object references
            csvFile: {
              ...file.csvFile,
            },
          };
        }
        // Skip uploaded files - they can't be restored
        return null;
      }).filter((file): file is NonNullable<typeof file> => file !== null),
    };

    const storedState: StoredMappingState = {
      version: STORAGE_VERSION,
      studyId,
      wizardState: serializedState,
      savedAt: new Date().toISOString(),
    };

    localStorage.setItem(getStorageKey(studyId), JSON.stringify(storedState));
  } catch (error) {
    console.error('Failed to save mapping state to localStorage:', error);
  }
};

/**
 * Load mapping state from localStorage for a specific study
 */
export const loadMappingState = (
  studyId: number
): ImportWizardState | null => {
  try {
    const stored = localStorage.getItem(getStorageKey(studyId));
    if (!stored) {
      return null;
    }

    const parsed: StoredMappingState = JSON.parse(stored);

    // Check version compatibility
    if (parsed.version !== STORAGE_VERSION) {
      clearMappingState(studyId);
      return null;
    }

    // Verify study ID matches
    if (parsed.studyId !== studyId) {
      clearMappingState(studyId);
      return null;
    }

    // Return the wizard state
    return parsed.wizardState;
  } catch (error) {
    console.error('Failed to load mapping state from localStorage:', error);
    clearMappingState(studyId);
    return null;
  }
};

/**
 * Clear mapping state from localStorage for a specific study
 */
export const clearMappingState = (studyId: number): void => {
  try {
    localStorage.removeItem(getStorageKey(studyId));
  } catch (error) {
    console.error('Failed to clear mapping state from localStorage:', error);
  }
};

/**
 * Clear all mapping states from localStorage
 */
export const clearAllMappingStates = (): void => {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.error('Failed to clear all mapping states from localStorage:', error);
  }
};

/**
 * Check if there's a saved mapping state for a specific study
 */
export const hasMappingState = (studyId: number): boolean => {
  try {
    const stored = localStorage.getItem(getStorageKey(studyId));
    return stored !== null;
  } catch {
    return false;
  }
};

